import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useWhatsAppCatalog, WppCatalogProduct, WppCatalogCollection } from "@/hooks/useWhatsAppCatalog";
import ProductFormModal, { ProductFormData } from "./ProductFormModal";
import CollectionFormModal, { CollectionFormData } from "./CollectionFormModal";

interface CatalogManagerPanelProps {
  sessionName: string;
  phone: string;
}

function resolveImageUrl(product: WppCatalogProduct): string | null {
  if (!product.image && (!product.images || product.images.length === 0)) return null;
  const src = product.image || product.images?.[0];
  if (!src) return null;
  if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("data:")) return src;
  return `data:image/jpeg;base64,${src}`;
}

export default function CatalogManagerPanel({ sessionName, phone }: CatalogManagerPanelProps) {
  const { t } = useTranslation("dashboard");
  const {
    products,
    collections,
    loading,
    actionLoading,
    error,
    cartEnabled,
    listProducts,
    listCollections,
    addProduct,
    editProduct,
    deleteProduct,
    setProductVisibility,
    changeProductImage,
    createCollection,
    editCollection,
    deleteCollection,
    setCartEnabled,
    refreshAll,
  } = useWhatsAppCatalog(sessionName, phone);

  const [activeTab, setActiveTab] = useState<"products" | "collections">("products");
  const [searchQuery, setSearchQuery] = useState("");
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<WppCatalogProduct | null>(null);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState<WppCatalogCollection | null>(null);
  const [collectionFilter, setCollectionFilter] = useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
  const [showConfirmDeleteCollection, setShowConfirmDeleteCollection] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (!sessionName || !phone || initialLoadDone.current) return;
    initialLoadDone.current = true;
    refreshAll();
  }, [sessionName, phone, refreshAll]);

  const showToast = useCallback((msg: string) => {
    setLocalError(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setLocalError(null), 4000);
  }, []);

  const filteredProducts = products.filter((p) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const name = p.name?.toLowerCase() || "";
    const desc = p.description?.toLowerCase() || "";
    const sku = p.retailerId?.toLowerCase() || "";
    return name.includes(q) || desc.includes(q) || sku.includes(q);
  });

  const collectionFilteredProducts = collectionFilter
    ? filteredProducts.filter((p) => {
        const col = collections.find((c) => c.id === collectionFilter);
        return col?.products?.includes(p.id);
      })
    : filteredProducts;

  const handleAddProduct = useCallback(
    async (form: ProductFormData) => {
      const res = await addProduct(form);
      if (res && res.id) {
        setShowProductModal(false);
        await listProducts();
        showToast(t("catalog.productAdded"));
      } else {
        const reason = res?.error || "";
        showToast(reason ? `${t("catalog.productAddFailed")}: ${reason}` : t("catalog.productAddFailed"));
      }
    },
    [addProduct, listProducts, showToast, t],
  );

  const handleEditProduct = useCallback(
    async (form: ProductFormData) => {
      if (!editingProduct) return;
      const ok = await editProduct(editingProduct.id, {
        name: form.name,
        description: form.description,
        price: form.price,
        currency: form.currency,
        url: form.url,
        retailerId: form.retailerId,
      });
      if (ok) {
        if (form.image) {
          await changeProductImage(editingProduct.id, form.image);
        }
        setShowProductModal(false);
        setEditingProduct(null);
        await listProducts();
        showToast(t("catalog.productUpdated"));
      } else {
        showToast(t("catalog.productUpdateFailed"));
      }
    },
    [editingProduct, editProduct, listProducts, showToast, t],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDeleteProduct = useCallback(
    async (id: string) => {
      const ok = await deleteProduct(id);
      if (ok) {
        setShowConfirmDelete(null);
        showToast(t("catalog.productDeleted"));
      } else {
        showToast(t("catalog.productDeleteFailed"));
      }
    },
    [deleteProduct, showToast, t],
  );

  const handleToggleVisibility = useCallback(
    async (id: string, currentHidden: boolean) => {
      const ok = await setProductVisibility(id, currentHidden);
      if (ok) {
        showToast(currentHidden ? t("catalog.productShown") : t("catalog.productHidden"));
      } else {
        showToast(t("catalog.visibilityFailed"));
      }
    },
    [setProductVisibility, showToast, t],
  );

  const handleCreateCollection = useCallback(
    async (form: CollectionFormData) => {
      const res = await createCollection(form.name, form.productIds);
      if (res && res.id) {
        setShowCollectionModal(false);
        await listCollections();
        showToast(t("catalog.collectionCreated"));
      } else {
        showToast(t("catalog.collectionCreateFailed"));
      }
    },
    [createCollection, listCollections, showToast, t],
  );

  const handleEditCollection = useCallback(
    async (form: CollectionFormData) => {
      if (!editingCollection) return;
      const ok = await editCollection(editingCollection.id, {
        name: form.name,
        products: form.productIds,
      });
      if (ok) {
        setShowCollectionModal(false);
        setEditingCollection(null);
        await listCollections();
        showToast(t("catalog.collectionUpdated"));
      } else {
        showToast(t("catalog.collectionUpdateFailed"));
      }
    },
    [editingCollection, editCollection, listCollections, showToast, t],
  );

  const handleDeleteCollection = useCallback(
    async (id: string) => {
      const ok = await deleteCollection(id);
      if (ok) {
        setShowConfirmDeleteCollection(null);
        showToast(t("catalog.collectionDeleted"));
      } else {
        showToast(t("catalog.collectionDeleteFailed"));
      }
    },
    [deleteCollection, showToast, t],
  );

  const handleToggleCart = useCallback(
    async () => {
      const next = !(cartEnabled ?? false);
      const ok = await setCartEnabled(next);
      if (ok) {
        showToast(next ? t("catalog.cartEnabled") : t("catalog.cartDisabled"));
      } else {
        showToast(t("catalog.cartToggleFailed"));
      }
    },
    [cartEnabled, setCartEnabled, showToast, t],
  );

  const openAddProduct = () => {
    setEditingProduct(null);
    setShowProductModal(true);
  };

  const openEditProduct = (product: WppCatalogProduct) => {
    setEditingProduct(product);
    setShowProductModal(true);
  };

  const openAddCollection = () => {
    setEditingCollection(null);
    setShowCollectionModal(true);
  };

  const openEditCollection = (col: WppCatalogCollection) => {
    setEditingCollection(col);
    setShowCollectionModal(true);
  };

  const productModalInitial: Partial<ProductFormData> | undefined = editingProduct
    ? {
        name: editingProduct.name,
        description: editingProduct.description || "",
        price: editingProduct.price || "",
        currency: editingProduct.currency || "EGP",
        url: editingProduct.url || "",
        retailerId: editingProduct.retailerId || "",
        image: editingProduct.image || "",
      }
    : undefined;

  const collectionModalInitial: Partial<CollectionFormData> | undefined = editingCollection
    ? {
        name: editingCollection.name,
        productIds: editingCollection.products || [],
      }
    : undefined;

  return (
    <div className="h-full flex flex-col bg-background-50 rounded-2xl border border-background-200/50 overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-4 md:px-5 py-3 border-b border-background-200/60 bg-background-50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-lg text-foreground-950">{t("catalog.title")}</h2>
            <span className="text-xs text-foreground-400 bg-background-100 px-2.5 py-1 rounded-full">
              {products.length} {t("catalog.products")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "products" && (
              <div className="flex items-center gap-2 mr-1">
                <span className="text-xs text-foreground-500 hidden sm:inline">{t("catalog.cart")}</span>
                <button
                  onClick={handleToggleCart}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    cartEnabled ? "bg-primary-500" : "bg-background-200"
                  }`}
                  title={cartEnabled ? t("catalog.disableCart") : t("catalog.enableCart")}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                      cartEnabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            )}
            <button
              onClick={refreshAll}
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors disabled:opacity-50"
              title={t("catalog.refresh")}
            >
              <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`}></i>
            </button>
          </div>
        </div>

        {/* Sub tabs */}
        <div className="flex items-center gap-1 mt-3 bg-background-100/50 p-1 rounded-full w-fit">
          <button
            onClick={() => setActiveTab("products")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === "products"
                ? "bg-background-50 text-foreground-950 shadow-sm"
                : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-shopping-bag-3-line text-sm"></i>
            {t("catalog.tabProducts")}
          </button>
          <button
            onClick={() => setActiveTab("collections")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === "collections"
                ? "bg-background-50 text-foreground-950 shadow-sm"
                : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-stack-line text-sm"></i>
            {t("catalog.tabCollections")}
          </button>
        </div>
      </div>

      {/* Toast */}
      {localError && (
        <div className="flex-shrink-0 mx-4 mt-3 px-4 py-2.5 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line text-sm"></i>
          <span className="flex-1">{localError}</span>
          <button onClick={() => setLocalError(null)} className="text-red-400 hover:text-red-600 cursor-pointer">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}
      {error && !localError && (
        <div className="flex-shrink-0 mx-4 mt-3 px-4 py-2.5 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line text-sm"></i>
          <span className="flex-1">{error}</span>
        </div>
      )}

      {/* Products Tab */}
      {activeTab === "products" && (
        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
            <div className="relative flex-1 w-full sm:w-auto">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("catalog.searchProducts")}
                className="w-full sm:w-64 pl-9 pr-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {collections.length > 0 && (
                <select
                  value={collectionFilter || ""}
                  onChange={(e) => setCollectionFilter(e.target.value || null)}
                  className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                >
                  <option value="">{t("catalog.allProducts")}</option>
                  {collections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
              <button
                onClick={openAddProduct}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap ml-auto sm:ml-0"
              >
                <i className="ri-add-line text-sm"></i>
                {t("catalog.addProduct")}
              </button>
            </div>
          </div>

          {/* Product Grid */}
          {collectionFilteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-shopping-bag-3-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">
                {searchQuery || collectionFilter ? t("catalog.noSearchResults") : t("catalog.noProducts")}
              </p>
              <p className="text-foreground-400 text-xs mt-1">{t("catalog.addFirstProduct")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {collectionFilteredProducts.map((product) => {
                const imgUrl = resolveImageUrl(product);
                const isHidden = product.isHidden ?? false;
                return (
                  <div
                    key={product.id}
                    className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden hover:border-background-300/80 transition-colors"
                  >
                    <div className="relative w-full h-40 bg-background-100 flex items-center justify-center overflow-hidden">
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <i className="ri-image-line text-3xl text-foreground-300"></i>
                          <span className="text-xs text-foreground-300">{t("catalog.noImage")}</span>
                        </div>
                      )}
                      {isHidden && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-foreground-800/80 text-white text-[10px] font-medium">
                          {t("catalog.hidden")}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-foreground-950 truncate" title={product.name}>
                        {product.name}
                      </h4>
                      <div className="flex items-center justify-between mt-1.5 mb-2">
                        <span className="text-sm font-medium text-primary-600">
                          {product.price ? `${product.price} ${product.currency || ""}` : "-"}
                        </span>
                        {product.retailerId && (
                          <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">
                            SKU: {product.retailerId}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openEditProduct(product)}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                          title={t("catalog.edit")}
                        >
                          <i className="ri-edit-line text-xs"></i>
                          {t("catalog.edit")}
                        </button>
                        <button
                          onClick={() => handleToggleVisibility(product.id, isHidden)}
                          disabled={actionLoading === "visibility"}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                          title={isHidden ? t("catalog.show") : t("catalog.hide")}
                        >
                          <i className={`${isHidden ? "ri-eye-line" : "ri-eye-off-line"} text-xs`}></i>
                          {isHidden ? t("catalog.show") : t("catalog.hide")}
                        </button>
                        <button
                          onClick={() => setShowConfirmDelete(product.id)}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          title={t("catalog.delete")}
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                          {t("catalog.delete")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Collections Tab */}
      {activeTab === "collections" && (
        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-foreground-500">
              {t("catalog.collectionCount", { count: collections.length })}
            </p>
            <button
              onClick={openAddCollection}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-sm"></i>
              {t("catalog.createCollection")}
            </button>
          </div>

          {collections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-stack-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">{t("catalog.noCollections")}</p>
              <p className="text-foreground-400 text-xs mt-1">{t("catalog.createFirstCollection")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {collections.map((col) => (
                <div
                  key={col.id}
                  className="flex items-center justify-between p-4 rounded-xl border border-background-200/60 bg-background-50 hover:border-background-300/80 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-stack-line text-accent-600 text-sm"></i>
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-semibold text-foreground-950 truncate">{col.name}</h4>
                      <p className="text-xs text-foreground-400 mt-0.5">
                        {t("catalog.productsInCollection", { count: col.products?.length || 0 })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEditCollection(col)}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
                      title={t("catalog.edit")}
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setShowConfirmDeleteCollection(col.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-red-500 cursor-pointer transition-colors"
                      title={t("catalog.delete")}
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Product Modal */}
      <ProductFormModal
        isOpen={showProductModal}
        isEdit={!!editingProduct}
        initialData={productModalInitial}
        loading={!!actionLoading}
        onClose={() => {
          setShowProductModal(false);
          setEditingProduct(null);
        }}
        onSubmit={editingProduct ? handleEditProduct : handleAddProduct}
      />

      {/* Collection Modal */}
      <CollectionFormModal
        isOpen={showCollectionModal}
        isEdit={!!editingCollection}
        initialData={collectionModalInitial}
        products={products.map((p) => ({ id: p.id, name: p.name }))}
        loading={!!actionLoading}
        onClose={() => {
          setShowCollectionModal(false);
          setEditingCollection(null);
        }}
        onSubmit={editingCollection ? handleEditCollection : handleCreateCollection}
      />

      {/* Confirm Delete Product */}
      {showConfirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowConfirmDelete(null)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm p-5 shadow-lg">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-heading text-base text-foreground-950 text-center mb-1">
              {t("catalog.confirmDeleteProduct")}
            </h3>
            <p className="text-sm text-foreground-500 text-center mb-5">{t("catalog.cannotUndo")}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setShowConfirmDelete(null)}
                className="px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("catalog.cancel")}
              </button>
              <button
                onClick={() => handleDeleteProduct(showConfirmDelete)}
                className="px-5 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("catalog.confirmDelete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Collection */}
      {showConfirmDeleteCollection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowConfirmDeleteCollection(null)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm p-5 shadow-lg">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-heading text-base text-foreground-950 text-center mb-1">
              {t("catalog.confirmDeleteCollection")}
            </h3>
            <p className="text-sm text-foreground-500 text-center mb-5">{t("catalog.cannotUndo")}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setShowConfirmDeleteCollection(null)}
                className="px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("catalog.cancel")}
              </button>
              <button
                onClick={() => handleDeleteCollection(showConfirmDeleteCollection)}
                className="px-5 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("catalog.confirmDelete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}